/**
 * TaskActivitySubscriber — renders nothing.
 *
 * Owns the activity drawer's data lifecycle:
 *  - one REST baseline per (re)connect, never polling;
 *  - live `ActivityDelta` rows reduced into the store;
 *  - the module presence roster.
 *
 * Mounted only inside the TaskManagement routes, which is what makes presence
 * meaningful: this component's connection lifetime IS "the user has
 * TaskManagement open". Follow `src/domain/badges/TaskBadgeSubscriber.tsx` for
 * the connect/baseline shape.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useTaskPortalActivityFeedService,
  useDomainDashboard,
  type TaskActivityDeltaMessage,
  type TaskActivityPresenceSnapshotMessage,
  type TaskActivityPresenceChangeMessage,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useTaskActivityStore } from '@/stores/taskActivityStore';

const DEBUG = import.meta.env.DEV;
const log = (...args: unknown[]) => {
  if (DEBUG) console.log('[TaskActivitySubscriber]', ...args);
};

export function TaskActivitySubscriber() {
  const { user } = useEnrichedAuth();
  const feedService = useTaskPortalActivityFeedService();

  const setFeed = useTaskActivityStore((s) => s.setFeed);
  const applyDelta = useTaskActivityStore((s) => s.applyDelta);
  const setPresentUsers = useTaskActivityStore((s) => s.setPresentUsers);
  const addPresentUser = useTaskActivityStore((s) => s.addPresentUser);
  const removePresentUser = useTaskActivityStore((s) => s.removePresentUser);
  const reset = useTaskActivityStore((s) => s.reset);

  const enabled = !!user;

  const refetchBaseline = useCallback(async () => {
    try {
      const feed = await feedService.getFeed();
      log('baseline', { groups: feed.groups.length, unread: feed.totalUnread });
      setFeed(feed.groups ?? []);
    } catch (err) {
      // Leave the previous rows in place — deltas can still keep the drawer
      // moving, and the next reconnect retries.
      log('baseline fetch failed', err);
    }
  }, [feedService, setFeed]);

  useEffect(() => {
    if (!enabled) return;
    void refetchBaseline();
  }, [enabled, refetchBaseline]);

  const { isConnected, requestPresenceSnapshot } = useDomainDashboard('taskactivity', {
    autoConnect: enabled,
    // The activity channel carries no dashboard metrics; subscribing is still
    // required because that is where the hub sends the presence roster.
    autoSubscribe: enabled,
    onActivityDelta: (msg: TaskActivityDeltaMessage) => {
      if (!msg?.item) return;
      log('delta', msg.item.activityTypeId, msg.item.itemId);
      applyDelta(msg.item);
    },
    onPresenceSnapshot: (msg: TaskActivityPresenceSnapshotMessage) => {
      log('presence snapshot', msg.userIds?.length ?? 0);
      setPresentUsers(msg.userIds ?? []);
    },
    onPresenceJoined: (msg: TaskActivityPresenceChangeMessage) => {
      if (msg?.userId) addPresentUser(msg.userId);
    },
    onPresenceLeft: (msg: TaskActivityPresenceChangeMessage) => {
      if (msg?.userId) removePresentUser(msg.userId);
    },
  });

  // Re-baseline on reconnect: deltas published during the gap are gone, and the
  // fresh connection id has lost its presence group membership.
  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;

    if (isConnected && wasConnectedRef.current) {
      log('reconnected → refetch baseline');
      void refetchBaseline();
      void requestPresenceSnapshot();
    }

    if (isConnected) wasConnectedRef.current = true;
  }, [enabled, isConnected, refetchBaseline, requestPresenceSnapshot]);

  // Leaving TaskManagement clears the drawer's state, and disconnecting removes
  // this user from everyone else's presence roster.
  useEffect(() => () => reset(), [reset]);

  return null;
}
